import { useEffect, useState } from 'react';
import moment from 'moment';
import { metabaseService, MetabaseIframeParams } from '../utils/metabase';
import { DATE_FORMAT } from '../utils/dashboard/periodRange';

export interface UseMetabaseIframeResult {
    iframeUrl: string | null;
    loading: boolean;
    error: string | null;
}

/**
 * Traduce el rango natural de la interfaz (`from` <= `to`) al que espera la
 * question 105. Reproduce exactamente lo que hace `CreationHistoryFilter` en el
 * backoffice platform:
 *
 *     const date = moment(to).add(1, 'days').format(dateFormat);
 *     onChangeAllField({ creation_date_after: date, creation_date_before: from });
 *
 * Dos rarezas, ambas necesarias:
 *
 * 1. Los límites van cruzados. La question interpreta `creation_date_after`
 *    como el tope SUPERIOR. Medido contra el bridge el 2026-07-09:
 *      after=2025-03-31, before=2025-03-01 -> 23 filas, 391 objetos
 *      after=2025-03-01, before=2025-03-31 ->  2 filas,   0 objetos
 *
 * 2. El tope superior lleva un día extra. Sin él, el último día del rango sale
 *    a cero.
 *
 * No lo "arregles": el gráfico se rompe.
 */
const toQuestionDateBounds = (params: MetabaseIframeParams): MetabaseIframeParams => ({
    ...params,
    creationDateAfter: moment(params.creationDateBefore).add(1, 'days').format(DATE_FORMAT),
    creationDateBefore: params.creationDateAfter,
});

export const useMetabaseIframe = (
    questionId: number,
    params: MetabaseIframeParams,
): UseMetabaseIframeResult => {
    const [iframeUrl, setIframeUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Serializamos los params para compararlos por valor: el padre recrea el
    // objeto en cada render y unas deps por referencia dispararían un bucle.
    const paramsKey = JSON.stringify(params);

    useEffect(() => {
        let cancelled = false;
        const current: MetabaseIframeParams = JSON.parse(paramsKey);

        setLoading(true);
        setError(null);

        metabaseService
            .getQuestionIframeUrl(questionId, toQuestionDateBounds(current))
            .then((url) => {
                if (cancelled) return;
                setIframeUrl(url);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setIframeUrl(null);
                setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [questionId, paramsKey]);

    return { iframeUrl, loading, error };
};
